import { SignupByInviteLinkMutation } from "@src/@types/api.d";
import useStore from "@src/store";
import React, { Fragment, useMemo } from "react";

export type InvitationData = NonNullable<
  SignupByInviteLinkMutation["signupByInviteLink"]
>["invitation"];

interface SeatingReservationProps {
  invitation: InvitationData | null | undefined;
  setOpen: (value: boolean) => void;
}

export type SeatingType = NonNullable<
  NonNullable<
    SignupByInviteLinkMutation["signupByInviteLink"]
  >["invitation"]["seating"]
>;

export type SeatingEventPartType = NonNullable<
  NonNullable<
    SignupByInviteLinkMutation["signupByInviteLink"]
  >["invitation"]["seating"]
>[number]["event_part"];

function groupSeatsByEventPart(seating: SeatingType): Array<{
  key: string;
  eventPart: SeatingEventPartType;
  seats: SeatingType;
}> {
  const byPart = new Map<
    string,
    {
      eventPart: SeatingEventPartType;
      seats: SeatingType;
    }
  >();
  for (const seat of seating) {
    const key =
      seat.event_part_id != null
        ? String(seat.event_part_id)
        : seat.event_part?.name ?? "other";
    if (!byPart.has(key)) {
      byPart.set(key, {
        eventPart: seat.event_part ?? null,
        seats: [],
      });
    }
    byPart.get(key)!.seats.push(seat);
  }
  return Array.from(byPart.entries()).map(([key, value]) => ({
    key,
    ...value,
  }));
}

const SeatingReservation: React.FC<SeatingReservationProps> = ({
  invitation,
  setOpen,
}) => {
  const isPreviewMode = useStore((state) => state.isPreviewMode);

  const seating = invitation?.seating;
  const hasSeating = seating && seating.length > 0;
  const guestName = invitation
    ? `${invitation.first_name} ${invitation.last_name}`.trim()
    : "";

  const sections = useMemo(
    () => (hasSeating && seating ? groupSeatsByEventPart(seating) : []),
    [hasSeating, seating]
  );

  return (
    <Fragment>
      {isPreviewMode ? (
        <div className="flex flex-col gap-[32px] items-center w-full xs:w-[510px] mx-auto bg-white py-10 px-6 rounded-[30px]">
          <div className="flex flex-col gap-[8px] items-center">
            <p className="text-sm text-center text-[#71717A] mb-4">
              You are in preview mode. The seating information is not real and
              is for demonstration purposes only.
            </p>

            <h3 className="font-medium text-[26px]">Your Seating</h3>
            {
              <p className="text-[#141414] text-[18px] font-medium">
                Guest's name
              </p>
            }
            <p className="text-[#71717A] text-[14px]">
              Your reserved seat at the event
            </p>
          </div>

          <div className="flex flex-col gap-6 w-full max-h-[400px] overflow-y-auto">
            <div className="flex flex-col gap-4 border border-[#E5E5E5] rounded-[16px] p-5 bg-[#FAFAFA]">
              {/* Event Part Header (once per section) */}
              <div className="pb-3 border-b border-[#E5E5E5]">
                <span className="text-[#996600] text-[14px] font-semibold">
                  Wedding Ceremony
                </span>

                <p className="text-[#71717A] text-[12px] mt-1">
                  Eko Hotel & Suites, Victoria Island, Lagos
                </p>

                <p className="text-[#71717A] text-[11px] mt-1">10.00am</p>
              </div>

              {/* All seats for this event part */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-3 border border-[#E5E5E5] rounded-[12px] p-4 bg-white">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#996600]/15 flex items-center justify-center shrink-0">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="3"
                            y="8"
                            width="18"
                            height="10"
                            rx="2"
                            stroke="#996600"
                            strokeWidth="2"
                          />
                          <path
                            d="M7 8V6C7 4.89543 7.89543 4 9 4H15C16.1046 4 17 4.89543 17 6V8"
                            stroke="#996600"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-[11px]">Table</p>
                        <p className="text-[#141414] text-[16px] font-medium">
                          Table 1 - Groom's Friends
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#996600]/15 flex items-center justify-center shrink-0">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 2C6.44772 2 6 2.44772 6 3V11C6 11.5523 6.44772 12 7 12H17C17.5523 12 18 11.5523 18 11V3C18 2.44772 17.5523 2 17 2H7Z"
                            fill="#996600"
                          />
                          <path
                            d="M5 13C4.44772 13 4 13.4477 4 14V15C4 15.5523 4.44772 16 5 16H19C19.5523 16 20 15.5523 20 15V14C20 13.4477 19.5523 13 19 13H5Z"
                            fill="#996600"
                          />
                          <path
                            d="M6 16V20M18 16V20"
                            stroke="#996600"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[#71717A] text-[11px]">
                          Seat Number
                        </p>
                        <p className="text-[#141414] text-[16px] font-medium">
                          S1
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-[#71717A] text-[12px] text-center">
            Please proceed to your assigned seat when you arrive
          </p>
        </div>
      ) : hasSeating ? (
        <div className="flex flex-col gap-[32px] items-center w-full xs:w-[510px] mx-auto bg-white py-10 px-6 rounded-[30px]">
          <div className="flex flex-col gap-[8px] items-center">
            <h3 className="font-medium text-[26px]">Your Seating</h3>
            {guestName && (
              <p className="text-[#141414] text-[18px] font-medium">
                {guestName}
              </p>
            )}
            <p className="text-[#71717A] text-[14px]">
              {seating.length > 1
                ? `You have ${seating.length} reserved seats`
                : "Your reserved seat at the event"}
            </p>
          </div>

          <div className="flex flex-col gap-6 w-full max-h-[400px] overflow-y-auto">
            {sections.map((section) => (
              <div
                key={section.key}
                className="flex flex-col gap-4 border border-[#E5E5E5] rounded-[16px] p-5 bg-[#FAFAFA]"
              >
                {/* Event Part Header (once per section) */}
                {section.eventPart && (
                  <div className="pb-3 border-b border-[#E5E5E5]">
                    <span className="text-[#996600] text-[14px] font-semibold">
                      {section.eventPart.name}
                    </span>
                    {section.eventPart.address && (
                      <p className="text-[#71717A] text-[12px] mt-1">
                        {section.eventPart.address}
                      </p>
                    )}
                    {section.eventPart.time && (
                      <p className="text-[#71717A] text-[11px] mt-1">
                        {new Date(section.eventPart.time).toLocaleString(
                          "en-US",
                          {
                            dateStyle: "medium",
                            timeStyle: "short",
                          }
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* All seats for this event part */}
                <div className="flex flex-col gap-3">
                  {section.seats.map((seat, index) => (
                    <div
                      key={`${seat.event_table_seat_id}-${index}`}
                      className="flex flex-col gap-3 border border-[#E5E5E5] rounded-[12px] p-4 bg-white"
                    >
                      {(invitation?.admits ?? 1) > 1 && (
                        <div>
                          <span className="text-[#71717A] text-[12px] uppercase tracking-wider bg-[#F0F0F0] px-2 py-1 rounded">
                            Admit {seat.admit_index} of {invitation?.admits}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#996600]/15 flex items-center justify-center shrink-0">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="3"
                                y="8"
                                width="18"
                                height="10"
                                rx="2"
                                stroke="#996600"
                                strokeWidth="2"
                              />
                              <path
                                d="M7 8V6C7 4.89543 7.89543 4 9 4H15C16.1046 4 17 4.89543 17 6V8"
                                stroke="#996600"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="text-[#71717A] text-[11px]">Table</p>
                            <p className="text-[#141414] text-[16px] font-medium">
                              {seat.table_name}{" "}
                              {seat.table_alias ? `(${seat.table_alias})` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#996600]/15 flex items-center justify-center shrink-0">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7 2C6.44772 2 6 2.44772 6 3V11C6 11.5523 6.44772 12 7 12H17C17.5523 12 18 11.5523 18 11V3C18 2.44772 17.5523 2 17 2H7Z"
                                fill="#996600"
                              />
                              <path
                                d="M5 13C4.44772 13 4 13.4477 4 14V15C4 15.5523 4.44772 16 5 16H19C19.5523 16 20 15.5523 20 15V14C20 13.4477 19.5523 13 19 13H5Z"
                                fill="#996600"
                              />
                              <path
                                d="M6 16V20M18 16V20"
                                stroke="#996600"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="text-[#71717A] text-[11px]">
                              Seat Number
                            </p>
                            <p className="text-[#141414] text-[16px] font-medium">
                              S{seat.seat_number}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="text-[#71717A] text-[12px] text-center">
            Please proceed to your assigned seat when you arrive
          </p>
        </div>
      ) : (
        <div className="flex items-center justify-center w-full xs:h-[300px] xs:w-[510px] bg-white py-10 rounded-[30px]">
          <p className="text-center text-[#71717A]">
            No seating reservation found
          </p>
        </div>
      )}
    </Fragment>
  );
};

export default SeatingReservation;
